/**
 * MasterOrgSelector — lets a MASTER user scope the master panel to a specific
 * organization. The chosen org id is stored in `useMasterSystemStore` and the
 * API interceptor attaches it as `x-selected-organization-id` on `/system/*`
 * requests so the backend reads from that org's database.
 *
 * Picking "Plataforma (organização padrão)" clears the selection — no header is
 * sent and the backend falls back to the default org.
 */

import { memo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useOrganizations } from '@/features/system/hooks/useOrganizations';
import { useMasterSystemStore } from '@/stores/master-system.store';

/** Sentinel for the "platform default" option (Select values must be non-empty). */
const DEFAULT_OPTION = '__default__';

export const MasterOrgSelector = memo(function MasterOrgSelector() {
  const { data, isLoading } = useOrganizations();
  const selectedOrganizationId = useMasterSystemStore(
    (s) => s.selectedOrganizationId,
  );
  const setSelectedOrganizationId = useMasterSystemStore(
    (s) => s.setSelectedOrganizationId,
  );

  const organizations = data?.data ?? [];

  if (isLoading) {
    return <Skeleton className="h-9 w-56" />;
  }

  const handleValueChange = (value: string) => {
    setSelectedOrganizationId(value === DEFAULT_OPTION ? null : value);
  };

  return (
    <Select
      value={selectedOrganizationId ?? DEFAULT_OPTION}
      onValueChange={handleValueChange}
    >
      <SelectTrigger className="w-56" aria-label="Selecionar organização">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={DEFAULT_OPTION}>
          Plataforma (organização padrão)
        </SelectItem>
        {organizations.map((org) => (
          <SelectItem key={org.id} value={org.id}>
            {org.name || org.slug}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
});
